/**
 * Container for MetaTags
 *
 * @module components/MetaTags
 */
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { AppConstants, UrlSegments } from '../../constants/App';
import { SourceTypeUrlParams } from '../../constants/Catalog';
import {
  messages as m,
  metaTagDescriptions,
  metaTagsMyCollection,
  metaTagsProfile,
  metaTagsSearch,
  metaTagsSettings,
  metaTagsTitle,
  metaTagsUpgrade,
  pathName,
} from '../../constants/MetaTags';
import { getInDomain } from '../../domains/externalUtils';
import * as AppUtils from '../../utils';

export class MetaTags extends React.Component {
  static propTypes = {
    brandingType: PropTypes.string,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func,
    }),
    metaTagDescription: PropTypes.string,
    metaTagText: PropTypes.string,
    pathSegmentOne: PropTypes.string,
    pathSegmentThree: PropTypes.string,
    pathSegmentTwo: PropTypes.string,
    preserveServerSideTagsOnInitialLoad: PropTypes.bool,
  };

  combineMetaTagElements = (elements, textEnding) => {
    elements.push(textEnding);
    return elements.join(' - ');
  };

  getSearchDetails = pathSegment => {
    const {
      intl: { formatMessage: t },
    } = this.props;
    const elements = [t(metaTagsSearch.search)];
    if (pathSegment) {
      elements.unshift(pathSegment);
    }
    const title = this.combineMetaTagElements(elements, t(m.pandora));
    return {
      title,
      description: title,
    };
  };

  getBrowseDetails = () => {
    const {
      intl: { formatMessage: t },
    } = this.props;
    return {
      title: t(metaTagsTitle.browse),
      description: t(metaTagDescriptions.browse),
    };
  };

  getGenreDetails = () => {
    const {
      intl: { formatMessage: t },
      metaTagText,
      metaTagDescription,
    } = this.props;
    const title = `${metaTagText} ${t(m.pandoraEnding)}`;
    return {
      title,
      description: `${title}. ${metaTagDescription}`,
    };
  };

  getBackstagePageDetails = (isCurator = false) => {
    const {
      intl: { formatMessage: t },
      metaTagText,
    } = this.props;
    const title = `${metaTagText} ${t(metaTagsTitle.backstagePageEnding)}`;
    const description = isCurator
      ? t(metaTagDescriptions.backstageCuratorPage, {
          curator: metaTagText,
        })
      : t(metaTagDescriptions.backstagePage, {
          artist: metaTagText,
        });
    return {
      title,
      description,
    };
  };

  getMetaTagDetails = (pathSegmentTwo, pathSegmentThree) => {
    const {
      intl: { formatMessage: t },
      metaTagText,
    } = this.props;
    const elements =
      pathSegmentThree === SourceTypeUrlParams.THUMBED_UP
        ? `${metaTagText} ${t(m.nowPlayingThumbedUp)}`
        : metaTagText;

    let textEnding = t(m.pandora);
    if (pathSegmentTwo === UrlSegments.PLAY) {
      textEnding = t(m.nowPlayingOnPandora);
    }

    const title = AppUtils.StringUtils.decodeHtml(
      this.combineMetaTagElements([elements], textEnding),
    );
    return {
      title,
      description: title,
    };
  };

  getSettingUpgradeDetails = () => {
    const {
      intl: { formatMessage: t },
      brandingType,
    } = this.props;
    switch (brandingType) {
      case AppConstants.BRANDING_PANDORA_PLUS:
        return {
          title: t(metaTagsUpgrade.upgradePremium),
          description: t(metaTagDescriptions.upgradePremium),
        };
      default:
        return {
          title: t(metaTagsUpgrade.upgrade),
          description: `${t(metaTagDescriptions.upgradePremium)} ${t(
            metaTagDescriptions.upgradePlus,
          )}`,
        };
    }
  };

  getUpgradeDetails = pathSegment => {
    const {
      intl: { formatMessage: t },
    } = this.props;
    switch (pathSegment) {
      case pathName.PREMIUM:
        return {
          title: t(metaTagsUpgrade.upgradePremium),
          description: t(metaTagDescriptions.upgradePremium),
        };
      case pathName.PLUS:
        return {
          title: t(metaTagsUpgrade.upgradePlus),
          description: t(metaTagDescriptions.upgradePlus),
        };
      default:
        return {
          title: t(metaTagsUpgrade.upgrade),
          description: `${t(metaTagDescriptions.upgradePremium)} ${t(
            metaTagDescriptions.upgradePlus,
          )}`,
        };
    }
  };

  getPremiumDetails = () => {
    const {
      intl: { formatMessage: t },
    } = this.props;
    return {
      title: t(metaTagsUpgrade.premium),
      description: t(metaTagDescriptions.premium),
    };
  };

  getMyCollectionDetails = pathSegmentTwo => {
    const {
      intl: { formatMessage: t },
    } = this.props;
    const titleElements = [t(metaTagsMyCollection.myCollection)];

    switch (pathSegmentTwo) {
      case pathName.ARTISTS:
        titleElements.push(t(metaTagsMyCollection.artists));
        break;
      case pathName.ALBUMS:
        titleElements.push(t(metaTagsMyCollection.albums));
        break;
      case pathName.SONGS:
        titleElements.push(t(metaTagsMyCollection.songs));
        break;
      case pathName.STATIONS:
        titleElements.push(t(metaTagsMyCollection.stations));
        break;
      case pathName.PLAYLISTS:
        titleElements.push(t(metaTagsMyCollection.playlists));
        break;
      default:
        titleElements.push(t(metaTagsMyCollection.all));
        break;
    }
    const title = this.combineMetaTagElements(titleElements, t(m.pandora));
    return {
      title,
      description: title,
    };
  };

  getProfileDetails = pathSegment => {
    const {
      intl: { formatMessage: t },
      metaTagText,
    } = this.props;
    const titleElements = [metaTagText];
    switch (pathSegment) {
      case pathName.THUMBS:
        titleElements.push(t(metaTagsProfile.thumbs));
        break;
      default:
        titleElements.push(t(metaTagsProfile.profile));
        break;
    }
    const title = this.combineMetaTagElements(titleElements, t(m.pandora));
    return {
      title,
      description: title,
    };
  };

  getSettingsDetails = pathSegment => {
    const {
      intl: { formatMessage: t },
    } = this.props;
    const titleElements = [t(metaTagsSettings.settings)];
    switch (pathSegment) {
      case pathName.INFO: // Account
        titleElements.push(t(metaTagsSettings.account));
        break;
      case pathName.NOTICES: // Notifications
        titleElements.push(t(metaTagsSettings.notifications));
        break;
      case pathName.CONTENT:
        titleElements.push(t(metaTagsSettings.content));
        break;
      case pathName.SUBSCRIPTION:
        titleElements.push(t(metaTagsSettings.subscription));
        break;
      case pathName.REDEEM:
        titleElements.push(t(metaTagsSettings.redeem));
        break;
      case pathName.UPGRADE:
        return this.getSettingUpgradeDetails();
    }
    const title = this.combineMetaTagElements(titleElements, t(m.pandora));
    return {
      title,
      description: title,
    };
  };

  getMetaDetails = () => {
    let metaDetails;
    const {
      intl: { formatMessage: t },
      pathSegmentOne,
      pathSegmentTwo,
      pathSegmentThree,
    } = this.props;

    switch (pathSegmentOne) {
      case pathName.COLLECTION:
        metaDetails = this.getMyCollectionDetails(pathSegmentTwo);
        break;
      // Browse and Deep Genres
      case pathName.BROWSE:
      case pathName.MUSIC:
        metaDetails = this.getBrowseDetails();
        break;
      // Genre
      case pathName.GENRE:
        metaDetails = this.getGenreDetails();
        break;
      case pathName.ARTIST: // BSP
        metaDetails = this.getBackstagePageDetails();
        break;
      case pathName.CURATOR:
        metaDetails = this.getBackstagePageDetails(true);
        break;
      case pathName.STATION:
      case pathName.ALBUM: // T3 specific
      case pathName.SONG: // T3 specific
      case pathName.PLAYLIST: // T3 specific
        metaDetails = this.getMetaTagDetails(pathSegmentTwo, pathSegmentThree);
        break;
      // Shared
      case pathName.SEARCH:
        metaDetails = this.getSearchDetails(pathSegmentTwo);
        break;
      case pathName.SETTINGS:
        metaDetails = this.getSettingsDetails(pathSegmentTwo);
        break;
      case pathName.PROFILE:
        metaDetails = this.getProfileDetails(pathSegmentTwo);
        break;
      // Upgrade Specific
      // case 'plus': this lives in a separate project.
      case pathName.UPGRADE:
        metaDetails = this.getUpgradeDetails(pathSegmentTwo);
        break;
      case pathName.PREMIUM:
        metaDetails = this.getPremiumDetails();
        break;
      default:
        metaDetails = {
          title: t(metaTagsTitle.default),
          description: t(metaTagDescriptions.default),
        };
        break;
    }
    return metaDetails;
  };

  render() {
    const { title, description } = this.getMetaDetails();
    if (this.props.preserveServerSideTagsOnInitialLoad) {
      return null;
    }

    return (
      <Helmet
        title={title}
        meta={[
          {
            name: 'description',
            content: description,
          },
        ]}
      />
    );
  }
}

export function mapStateToProps(state) {
  const pathname = decodeURIComponent(window.location.pathname);
  const paths = pathname.replace(/^\//, '').split('/');
  const [pathSegmentOne, pathSegmentTwo, pathSegmentThree] = paths;
  const brandingType = AppUtils.Listener.getBranding(state);
  let metaTagText;
  let metaTagDescription;
  // Temporarily only preserving server-side metaTags for artist and song bsp for SEO title metatag test.
  // This will extend to other pages as part of a larger task to move StructuredMetadata generation on server-side: WEBCLIENT-4462
  const isInitialLoad = getInDomain('application.utils.isInitialLoad')(state);
  const preserveServerSideTagsOnInitialLoad =
    isInitialLoad &&
    (AppUtils.Route.isBackstageArtistPath(pathname) ||
      AppUtils.Route.isBackstageTrackPath(pathname) ||
      AppUtils.Route.isPlaylistPath(pathname) ||
      AppUtils.Route.isNowPlayingPlaylistRoute(pathname) ||
      AppUtils.Route.isOnUpgradePage() ||
      AppUtils.Route.isOnPlansPage());
  switch (pathSegmentOne) {
    case pathName.ARTIST:
    case pathName.CURATOR:
    case pathName.SONG:
    case pathName.ALBUM:
    case pathName.PLAYLIST: {
      const getAnnotations = getInDomain('catalog.utils.getAnnotations');
      const getAnnotationByEncryptedPandoraId = getInDomain(
        'catalog.utils.getAnnotationByEncryptedPandoraId',
      );
      const pandoraId = paths[paths.length - 1];
      const activeAnnotations = pandoraId.includes(':')
        ? getAnnotations(state, pandoraId)
        : getAnnotationByEncryptedPandoraId(state, pandoraId);
      const sourceName = activeAnnotations.get('name');
      const owner = getAnnotations(state, activeAnnotations.get('listenerPandoraId'));
      const authorName = owner.isEmpty()
        ? activeAnnotations.get('artistName')
        : owner.get('displayname') || owner.get('webname');
      metaTagText =
        authorName && !(pathSegmentOne === pathName.CURATOR)
          ? `${sourceName} by ${authorName}`
          : `${sourceName}`;
      break;
    }
    case pathName.GENRE: {
      const genreSeoToken = pathSegmentThree;
      const genre = AppUtils.Music.getGenre(state, genreSeoToken);
      if (genre) {
        metaTagText = genre.get('name');
        metaTagDescription = genre.get('description');
      }
      break;
    }
    case pathName.STATION: {
      let stationId = isNil(paths[2]) ? paths[1] : paths[2];
      if (paths.includes(UrlSegments.PLAY)) {
        stationId = !paths.includes(SourceTypeUrlParams.THUMBED_UP)
          ? paths[2]
          : paths[3] && paths[3].split && paths[3].split(':').pop();
      }
      metaTagText = getInDomain('playback.utils.getStationName', '')(state, stationId);
      break;
    }
    case pathName.PROFILE: {
      const listener = AppUtils.Listener.getCurrentListenerFromState(state);
      metaTagText = (listener && listener.get('fullName')) || listener.get('webname');
      break;
    }
  }
  return {
    brandingType,
    metaTagDescription,
    metaTagText,
    pathSegmentOne,
    pathSegmentThree,
    pathSegmentTwo,
    preserveServerSideTagsOnInitialLoad,
  };
}

const enhance = compose(connect(mapStateToProps), injectIntl);

export default enhance(MetaTags);
